import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';
import { axiosGet } from '../utils/axiosGet';
import { useLoggedInUserContextProvider } from './LoggedInUserContext';
import { devError } from '../utils/devLog';

const AdminActionCollections = createContext();

export const AdminActionCollectionsProvider = ({ children }) => {
  const { originalUserDetails } = useLoggedInUserContextProvider();

  const [enableNewSubmission, setEnableNewSubmission] = useState(true);
  const [enableSubmissionEdit, setEnableSubmissionEdit] = useState(true);
  const [enableRefereeReportUploads, setEnableRefereeReportUploads] =
    useState(true);
  const [enableCommitteeAccess, setEnableCommitteeAccess] = useState(true);
  const [enableCOI, setEnableCOI] = useState(true);
  const [enableScoring, setEnableScoring] = useState(true);
  const [enableCouncilMembers, setEnableCouncilMembers] = useState(true);
  const [enableRecommendedToCouncil, setEnableRecommendedToCouncil] =
    useState(true);
  const [enableRefereeReminders, setEnableRefereeReminders] = useState(false);

  const memoizedUser = useMemo(
    () => originalUserDetails,
    [originalUserDetails?.id],
  );

  const checkAdminAction = useCallback(
    () =>
      axiosGet('admin-action').then((response) => {
        if (response && response?.data?.data?.attributes) {
          setEnableNewSubmission(
            response?.data?.data?.attributes?.enableNewSubmission,
          );
          setEnableSubmissionEdit(
            response?.data?.data?.attributes?.enableSubmissionEdit,
          );
          setEnableRefereeReportUploads(
            response?.data?.data?.attributes?.enableRefereeReportUploads,
          );
          setEnableCommitteeAccess(
            response?.data?.data?.attributes?.enableCommitteeAccess,
          );
          setEnableCOI(response?.data?.data?.attributes?.enableCOI);
          setEnableScoring(response?.data?.data?.attributes?.enableScoring);
          setEnableCouncilMembers(
            response?.data?.data?.attributes?.enableCouncilMembers,
          );
          setEnableRecommendedToCouncil(
            response?.data?.data?.attributes?.enableRecommendedToCouncil,
          );
          setEnableRefereeReminders(
            response?.data?.data?.attributes?.enableRefereeReminders,
          );

          return response;
        }
      }),
    [],
  );

  useEffect(() => {
    if (memoizedUser) {
      const fetchAdminActionInterval = () =>
        checkAdminAction()
          .then((response) => {
            if (response?.data?.data?.attributes?.refreshInterval) {
              const interval = setInterval(
                checkAdminAction,
                response.data.data.attributes.refreshInterval * 60 * 1000,
              );
              return () => clearInterval(interval);
            }
          })
          .catch((error) => {
            devError('Error fetching refresh interval:', error);
          });

      let cleanup;
      fetchAdminActionInterval().then((cleanupFn) => {
        cleanup = cleanupFn;
      });

      return () => {
        if (cleanup) cleanup();
      };
    }
  }, [memoizedUser, checkAdminAction]);

  return (
    <AdminActionCollections.Provider
      value={useMemo(
        () => ({
          enableNewSubmission,
          enableSubmissionEdit,
          enableRefereeReportUploads,
          enableCommitteeAccess,
          enableCOI,
          enableScoring,
          enableCouncilMembers,
          enableRecommendedToCouncil,
          enableRefereeReminders,
          refreshAdminAction: checkAdminAction,
        }),
        [
          enableNewSubmission,
          enableSubmissionEdit,
          enableRefereeReportUploads,
          enableCommitteeAccess,
          enableCOI,
          enableScoring,
          enableCouncilMembers,
          enableRecommendedToCouncil,
          enableRefereeReminders,
          checkAdminAction,
        ],
      )}
    >
      {children}
    </AdminActionCollections.Provider>
  );
};

AdminActionCollectionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAdminActionCollections = () =>
  useContext(AdminActionCollections);
